import { useCallback, useEffect, useRef } from 'react';
import { overlay } from 'overlay-kit';
import { useGetMyVouchers } from '@/api/gen/vouchers/vouchers.gen';
import { useUser } from '@/lib/auth';
import { CookieManager } from '@/shared/cookies';
import { isPhoneTrialUpsellEligible, isPhoneVerificationPromptSuppressed } from '@/shared/features/phone-verification/utils';
import { markPromotionModalShown, wasPromotionModalShown } from '@/shared/features/promotion-modal-state';
import { PhoneVerificationBannerModal } from '../components/PhoneVerificationBannerModal';
export const PHONE_VERIFICATION_BANNER_COOKIE = 'phone_verification_banner_dismissed';

/**
 * 휴대폰 인증 배너 모달을 관리하는 훅
 *
 * 다음 조건을 모두 만족할 때 모달을 표시합니다:
 * 1. 로그인한 사용자
 * 2. 휴대폰 인증을 하지 않은 사용자
 * 3. "오늘 하루동안 보지 않기" 쿠키가 설정되지 않은 경우
 * 4. 학생 계정이 아닌 경우
 * 5. KR 국가 사용자인 경우
 */
export const usePhoneVerificationBannerModal = ({
  enabled = true,
  force = false,
  source = 'banner_modal',
  onDismiss
}: {
  enabled?: boolean;
  force?: boolean;
  source?: 'signup' | 'banner_modal';
  onDismiss?: () => void | Promise<void>;
} = {}) => {
  const {
    user,
    isAuthenticated,
    isInitialized
  } = useUser();
  const {
    data: vouchersResponse,
    isLoading: isVouchersLoading
  } = useGetMyVouchers({
    usageStatus: 'UNUSED'
  }, {
    query: {
      enabled: enabled && isAuthenticated,
      throwOnError: false
    }
  });
  const hasOpenedRef = useRef(false);
  const isDismissedForToday = useCallback(() => {
    const cookieManager = new CookieManager(PHONE_VERIFICATION_BANNER_COOKIE);
    return cookieManager.has();
  }, []);
  const dismissForToday = useCallback(() => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);
    const cookieManager = new CookieManager(PHONE_VERIFICATION_BANNER_COOKIE, {
      expires: tomorrow
    });
    cookieManager.set('true');
  }, []);
  const openModal = useCallback(() => {
    requestAnimationFrame(() => {
      overlay.open(({
        isOpen,
        unmount
      }) => <PhoneVerificationBannerModal isOpen={isOpen} onClose={unmount} onDismiss={async () => {
        unmount();
        await onDismiss?.();
      }} onDismissForToday={dismissForToday} onOpen={markPromotionModalShown} source={source} />);
    });
  }, [dismissForToday, onDismiss, source]);
  const shouldShowModal = useCallback(() => {
    if (!enabled) return false;
    if (!isInitialized) return false;
    if (!isAuthenticated || !user) return false;
    if (isVouchersLoading) return false;
    if (!isPhoneTrialUpsellEligible(vouchersResponse?.data.trialOffer)) return false;
    // 본인이 「혜택없이 이용」으로 남긴 영구 거절은 force 로도 뚫지 않는다.
    // force 가 뚫어야 하는 것은 브라우저 단위 상태(오늘 하루 쿠키·세션 프로모션
    // 플래그)뿐이다 — 이전 계정·이전 세션의 흔적이 신규 가입자의 안내를 삼키면 안 되니까.
    // 반면 이 값은 quizUserId 별로 저장되고 가입 직후(force) 시점의 quizUserId 는
    // 방금 만들어진 것이라 원래 비어 있다. 값이 있다면 URL 에 trialSource=signup 이
    // 남은 채 본인이 이미 거절한 경우뿐이므로, 그 거절을 존중한다.
    if (isPhoneVerificationPromptSuppressed(user.quizUserId)) return false;
    if (!force && isDismissedForToday()) return false;
    if (!force && wasPromotionModalShown()) return false;
    return true;
  }, [isInitialized, enabled, force, isAuthenticated, isVouchersLoading, user, vouchersResponse, isDismissedForToday]);
  useEffect(() => {
    if (hasOpenedRef.current) return;
    if (shouldShowModal()) {
      hasOpenedRef.current = true;
      openModal();
    }
  }, [shouldShowModal, openModal]);
  return {
    openModal,
    dismissForToday,
    isDismissedForToday
  };
};